import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const TableField = ({ 
    field, 
    value = [], 
    onChange, 
    disabled = false,
    isEditing = false 
}) => {
    const [tableData, setTableData] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize table data from value or create initial empty row
    useEffect(() => {
        if (Array.isArray(value) && value.length > 0) {
            setTableData(value);
            setIsInitialized(true);
        } else if (!isInitialized) {
            // Create initial empty row based on column definitions
            const initialRow = {};
            field.columns?.forEach(column => {
                switch (column.type) {
                    case 'checkbox':
                        initialRow[column.id] = false;
                        break;
                    case 'number':
                        initialRow[column.id] = 0;
                        break;
                    default:
                        initialRow[column.id] = '';
                }
            });
            setTableData([{ ...initialRow, _id: Date.now() }]);
            setIsInitialized(true);
        }
    }, [field.columns, value, isInitialized]);

    // Update parent when table data changes (debounced to prevent flickering)
    useEffect(() => {
        if (!isInitialized) return;
        
        const timeoutId = setTimeout(() => {
            if (onChange) {
                onChange(field.id, tableData);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [tableData, field.id, onChange, isInitialized]);

    const addRow = () => {
        const newRow = { _id: Date.now() };
        field.columns?.forEach(column => {
            switch (column.type) {
                case 'checkbox':
                    newRow[column.id] = false;
                    break;
                case 'number':
                    newRow[column.id] = 0;
                    break;
                default:
                    newRow[column.id] = '';
            }
        });
        setTableData([...tableData, newRow]);
    };

    const removeRow = (rowIndex) => {
        if (tableData.length > 1) { // Keep at least one row
            setTableData(tableData.filter((_, index) => index !== rowIndex));
        }
    };

    const updateCell = (rowIndex, columnId, cellValue) => {
        setTableData(prevData => {
            const updatedData = [...prevData];
            updatedData[rowIndex] = { ...updatedData[rowIndex], [columnId]: cellValue };
            return updatedData;
        });
    };

    const renderCellInput = (column, rowIndex, cellValue) => {
        const handleChange = (newValue) => {
            updateCell(rowIndex, column.id, newValue);
        };

        const commonProps = {
            size: 'small',
            disabled: disabled || !isEditing,
            fullWidth: true,
            variant: 'standard'
        };

        switch (column.type) {
            case 'text':
                return (
                    <TextField
                        {...commonProps}
                        value={cellValue || ''}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={column.placeholder || ''}
                    />
                );

            case 'number':
                return (
                    <TextField
                        {...commonProps}
                        type="number"
                        value={cellValue || 0}
                        onChange={(e) => handleChange(Number(e.target.value))}
                        inputProps={{ min: column.min, max: column.max, step: column.step }}
                    />
                );

            case 'select':
                return (
                    <FormControl fullWidth size="small" variant="standard">
                        <Select
                            value={cellValue || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            disabled={disabled || !isEditing}
                        >
                            <MenuItem value="">Select...</MenuItem>
                            {column.options?.map((option, index) => (
                                <MenuItem key={index} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'date':
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            value={cellValue ? new Date(cellValue) : null}
                            onChange={(date) => handleChange(date ? date.toISOString().split('T')[0] : '')}
                            disabled={disabled || !isEditing}
                            renderInput={(params) => (
                                <TextField {...params} {...commonProps} />
                            )}
                        />
                    </LocalizationProvider>
                );

            case 'time':
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                            value={cellValue ? new Date(`2000-01-01T${cellValue}`) : null}
                            onChange={(time) => {
                                if (time) {
                                    const timeString = time.toTimeString().split(' ')[0].substring(0, 5);
                                    handleChange(timeString);
                                } else {
                                    handleChange('');
                                }
                            }}
                            disabled={disabled || !isEditing}
                            renderInput={(params) => (
                                <TextField {...params} {...commonProps} />
                            )}
                        />
                    </LocalizationProvider>
                );

            case 'datetime':
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                            value={cellValue ? new Date(cellValue) : null}
                            onChange={(datetime) => handleChange(datetime ? datetime.toISOString() : '')}
                            disabled={disabled || !isEditing}
                            renderInput={(params) => (
                                <TextField {...params} {...commonProps} />
                            )}
                        />
                    </LocalizationProvider>
                );

            case 'checkbox':
                return (
                    <Checkbox
                        checked={cellValue === true || cellValue === 'true'}
                        onChange={(e) => handleChange(e.target.checked)}
                        disabled={disabled || !isEditing}
                        size="small"
                    />
                );

            default:
                return (
                    <TextField
                        {...commonProps}
                        value={cellValue || ''}
                        onChange={(e) => handleChange(e.target.value)}
                    />
                );
        }
    };

    if (!field.columns || field.columns.length === 0) {
        return (
            <Box sx={(theme) => ({ p: 2, border: `1px dashed ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'background.paper' })}>
                <Typography color="text.secondary" align="center">
                    No columns configured for this table
                </Typography>
            </Box>
        );
    }

    return (
        <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ p: 2 }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                {field.columns.map((column) => (
                                    <TableCell key={column.id} align="left">
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {column.label}
                                            {column.required && (
                                                <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {column.type}
                                        </Typography>
                                    </TableCell>
                                ))}
                                {(isEditing && !disabled && tableData.length > 1) && (
                                    <TableCell width={60} align="center">
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, rowIndex) => (
                                <TableRow key={row._id || rowIndex} hover>
                                    {field.columns.map((column) => (
                                        <TableCell key={column.id}>
                                            {renderCellInput(column, rowIndex, row[column.id])}
                                        </TableCell>
                                    ))}
                                    {(isEditing && !disabled && tableData.length > 1) && (
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => removeRow(rowIndex)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
                
                {(isEditing && !disabled) && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addRow}
                            variant="outlined"
                            size="small"
                        >
                            Add Row
                        </Button>
                    </Box>
                )}

                {!isEditing && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {tableData.length} row{tableData.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default TableField;
